import { useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, CardHeader, CardTitle, Alert, PasswordToggleButton } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { data?: { detail?: string | { code?: string } } } };
    const detail = axiosErr.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'code' in detail) {
      return detail.code ?? null;
    }
  }
  return null;
}

function getApiStatusCode(err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { status?: number } };
    return axiosErr.response?.status ?? null;
  }
  return null;
}

function validatePassword(value: string): string | null {
  if (value.length < 12) return 'Password must be at least 12 characters.';
  if (!/[a-zA-Z]/.test(value)) return 'Password must contain at least one letter.';
  if (!/[0-9]/.test(value)) return 'Password must contain at least one digit.';
  return null;
}

// ---------------------------------------------------------------------------
// Section — Change password
// ---------------------------------------------------------------------------

function ChangePasswordSection() {
  const { user, refreshUser } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Client-side validation (shown inline before submit)
  const passwordError = newPassword ? validatePassword(newPassword) : null;
  const confirmError =
    confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match.' : null;
  const sameAsCurrent =
    currentPassword && newPassword && currentPassword === newPassword
      ? 'New password must differ from current.'
      : null;

  const canSubmit =
    !isSubmitting &&
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    !passwordError &&
    !confirmError &&
    !sameAsCurrent;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setStatus('idle');
    setErrorMsg(null);

    try {
      await apiClient.post('/auth/me/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // If user had must_change_password, refresh so the flag clears in context
      if (user?.must_change_password) {
        await refreshUser();
      }
    } catch (err) {
      setStatus('error');
      const statusCode = getApiStatusCode(err);
      const code = getApiErrorCode(err);

      if (statusCode === 401 || code === 'invalid_current_password') {
        setErrorMsg('Current password is incorrect.');
      } else if (statusCode === 400 || code === 'same_password') {
        setErrorMsg('New password must differ from current.');
      } else if (statusCode === 422) {
        const detail = code ?? 'Password does not meet requirements.';
        setErrorMsg(detail);
      } else {
        setErrorMsg('Failed to change password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Password</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Current password */}
          <div>
            <label
              htmlFor="settings-current-password"
              style={{
                display: 'block',
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginBottom: '6px',
              }}
            >
              Current password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="settings-current-password"
                type={showCurrentPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setStatus('idle');
                }}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-strong)',
                  backgroundColor: 'var(--color-surface-raised-3)',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  padding: '0 40px 0 14px',
                  boxSizing: 'border-box',
                  transition: 'border-color 180ms ease, box-shadow 180ms ease',
                }}
              />
              <PasswordToggleButton
                visible={showCurrentPassword}
                onToggle={() => setShowCurrentPassword((v) => !v)}
                disabled={isSubmitting}
                controls="settings-current-password"
              />
            </div>
            {sameAsCurrent && (
              <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', marginTop: '4px' }}>
                {sameAsCurrent}
              </p>
            )}
          </div>

          {/* New password */}
          <div>
            <label
              htmlFor="settings-new-password"
              style={{
                display: 'block',
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginBottom: '6px',
              }}
            >
              New password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="settings-new-password"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="At least 12 characters, letters + digits"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setStatus('idle');
                }}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-strong)',
                  backgroundColor: 'var(--color-surface-raised-3)',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  padding: '0 40px 0 14px',
                  boxSizing: 'border-box',
                  transition: 'border-color 180ms ease, box-shadow 180ms ease',
                }}
              />
              <PasswordToggleButton
                visible={showNewPassword}
                onToggle={() => setShowNewPassword((v) => !v)}
                disabled={isSubmitting}
                controls="settings-new-password"
              />
            </div>
            {passwordError && (
              <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', marginTop: '4px' }}>
                {passwordError}
              </p>
            )}
          </div>

          {/* Confirm new password */}
          <div>
            <label
              htmlFor="settings-confirm-password"
              style={{
                display: 'block',
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginBottom: '6px',
              }}
            >
              Confirm new password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="settings-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Repeat new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setStatus('idle');
                }}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-strong)',
                  backgroundColor: 'var(--color-surface-raised-3)',
                  color: 'var(--color-text-primary)',
                  fontSize: '14px',
                  padding: '0 40px 0 14px',
                  boxSizing: 'border-box',
                  transition: 'border-color 180ms ease, box-shadow 180ms ease',
                }}
              />
              <PasswordToggleButton
                visible={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((v) => !v)}
                disabled={isSubmitting}
                controls="settings-confirm-password"
              />
            </div>
            {confirmError && (
              <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', marginTop: '4px' }}>
                {confirmError}
              </p>
            )}
          </div>

          {status === 'success' && <Alert variant="success">Password changed successfully.</Alert>}
          {status === 'error' && errorMsg && <Alert variant="error">{errorMsg}</Alert>}

          <div>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {isSubmitting ? 'Saving...' : 'Change password'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------

function Settings() {
  const { user } = useAuth();
  const mustChange = user?.must_change_password === true;

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-text-muted">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <PageHeader title="Settings" description="Manage your account settings" />

      {mustChange && (
        <div
          role="alert"
          style={{
            borderRadius: '10px',
            border: '1px solid rgba(251, 191, 36, 0.40)',
            backgroundColor: 'rgba(251, 191, 36, 0.08)',
            padding: '14px 16px',
            fontSize: '14px',
            color: 'var(--color-signal-warn, #fbbf24)',
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          You must change your password before continuing.
        </div>
      )}

      <ChangePasswordSection />

      {!mustChange && (
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '-8px' }}>
          To update your display name or email, visit your{' '}
          <a href="/profile" style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}>
            Profile
          </a>.
        </p>
      )}
    </div>
  );
}

export default Settings;
